import { StudyConfig } from '@csp/csp-common-config';
import { BioSampleContainerQueryService, BioSampleQueryMapper, BioSampleQueryService } from '@csp/csp-common-hbs';
import { BioSample, KitTypeAndDescriptions } from '@csp/csp-common-hbs-model';
import { ApiOptions, CspError, FreeTextCriteria, Maybe, Patient, Study, UserId } from '@csp/csp-common-model';
import { CachedPatientQueryService, PatientQueryMapper, PatientQueryService } from '@csp/csp-common-patient';
import { ScheduleRequestRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import {
  BookedVisitsPage,
  VisitRequest,
  VisitScheduleRolloutService,
  VisitScheduleService,
  VisitService,
} from '@csp/csp-common-visits';
import { Page, largePage } from '@csp/dmdp-api-common-dto';
import { UserIncludeFieldType } from '@csp/dmdp-api-user-dto';
import uniq from 'lodash/uniq';
import { StaticContentService } from '../../../../common/service/StaticContentService';
import { StudyContentService } from '../../../../common/service/StudyContentService';
import { BookedPatientVisitMapper } from '../../../patient-visits/mapper/BookedPatientVisitMapper';
import { BookedPatientVisit } from '../../../patient-visits/model/BookedPatientVisit';
import { BookedPatientVisitIncludeFields } from '../model/BookedPatientVisitIncludeFields';
import { BookedPatientVisitItemPage } from '../model/BookedPatientVisitItemPage';
import { BookedPatientVisitQueryIn } from '../model/BookedPatientVisitQueryIn';

const queryPatientsByBookedVisits = async (
  bookedVisits: BookedVisitsPage,
  study: Study,
  studyConfig: StudyConfig,
): Promise<Patient[]> => {
  const userIds = bookedVisits.visits.flatMap(bookedVisit =>
    bookedVisit.participants.flatMap(participant => participant.userId),
  );

  const uniqueUserIds = uniq(userIds);

  if (uniqueUserIds.length) {
    return await CachedPatientQueryService.getPatientsByUserIds(
      uniqueUserIds,
      PatientQueryMapper.defaultFields,
      studyConfig,
      study.sites,
    );
  } else {
    return [];
  }
};

const queryParticipantIdsByFreeText = async (freeText: FreeTextCriteria): Promise<UserId[]> => {
  const patientsPage = await PatientQueryService.query({
    pageSize: Page.maxLimit,
    criteria: { freeText },
    fields: [UserIncludeFieldType.USER_ID, UserIncludeFieldType.LOCALIZATION],
    key: 'participant-ids-by-free-text',
  });

  return patientsPage.data?.map(patient => patient.userId);
};

const queryVisitRequestsByPatients = async (
  patients: Patient[],
  studyConfig?: StudyConfig,
): Promise<VisitRequest[]> => {
  const siteIds = patients.map(patient => patient.firstOrgId).filter(isDefined);
  const uniqueSiteIds = uniq(siteIds);

  return await VisitScheduleService.getCachedActivatedRequestsByOrgIds(uniqueSiteIds, studyConfig);
};

const evaluateFreeTextResults = (
  freeTextCriteria: Maybe<FreeTextCriteria>,
  participantIds: Maybe<UserId[]>,
): { hasNoResults: boolean; hasTooManyResults: boolean } => {
  const hasFreeTextCriteria = Boolean(freeTextCriteria?.text);
  const hasNoResults = hasFreeTextCriteria && participantIds?.length === 0;
  const hasTooManyResults = hasFreeTextCriteria && participantIds ? participantIds.length >= Page.maxLimit : false;

  return { hasNoResults, hasTooManyResults };
};

const queryBioSamplesIfEnabled = async (
  patientVisitQueryIn: BookedPatientVisitQueryIn,
  visitRequests: VisitRequest[],
  bookedVisits: BookedVisitsPage,
): Promise<BioSample[]> => {
  const bioSamplesForAllVisits: BioSample[] = [];
  if (patientVisitQueryIn.includeFields?.bioSamples) {
    const hbsEnabledVisitRequests = visitRequests.filter(req => req.hbs.enabled);
    const visitIds = bookedVisits.visits
      .filter(visit => hbsEnabledVisitRequests.some(req => ScheduleRequestRef.isSameRequestRef(visit.requestRef, req)))
      .map(bookedVisit => bookedVisit.visitId);
    if (visitIds.length) {
      const sampleResult = await BioSampleQueryService.query(
        {
          pageSize: Page.maxLimit,
          criteria: {
            visitIds,
          },
          sort: BioSampleQueryMapper.defaultSorting,
        },
        undefined,
      );
      bioSamplesForAllVisits.push(...sampleResult.data);
    }
  }
  return bioSamplesForAllVisits;
};

const queryKitDescByTypesIfEnabled = async (
  visitRequests: VisitRequest[],
  patientVisitQueryIn: BookedPatientVisitQueryIn,
): Promise<Maybe<KitTypeAndDescriptions>> => {
  const hbsEnabledVisitRequests = visitRequests?.filter(req => req.hbs.enabled);
  if (patientVisitQueryIn.includeFields?.supportedKits && hbsEnabledVisitRequests) {
    const allKitTypes = visitRequests.flatMap(visit => visit.hbs.kitTypes) || [];
    return await BioSampleContainerQueryService.queryCachedKitDescriptionByTypes(allKitTypes);
  } else {
    return undefined;
  }
};

const query = async (
  study: Study,
  studyConfig: StudyConfig,
  patientVisitQueryIn: BookedPatientVisitQueryIn,
  page: Page,
  apiOptions?: ApiOptions,
): Promise<BookedPatientVisitItemPage> => {
  const unitTimeTexts = StaticContentService.getGenericBlock('webHcpStatic', 'timeUnits');
  const patientVisitTexts = StaticContentService.getGenericEntry('webHcpPatientVisit');
  const freeTextCriteria = patientVisitQueryIn?.criteria?.freeText;
  const participantIds = freeTextCriteria?.text
    ? await queryParticipantIdsByFreeText(freeTextCriteria)
    : patientVisitQueryIn?.criteria?.participantIds;

  const freeTextResultsEvaluation = evaluateFreeTextResults(freeTextCriteria, participantIds);

  const allVisitsRequests = await VisitScheduleService.getCachedActivatedRequestsByOrgIds(
    study.sites.map(site => site.orgId),
    studyConfig,
  );

  if (freeTextResultsEvaluation.hasNoResults || freeTextResultsEvaluation.hasTooManyResults) {
    return {
      bookedVisits: [],
      pageSize: 0,
      total: 0,
      hasTooManyFreeTextResults: freeTextResultsEvaluation.hasTooManyResults,
      next: undefined,
    };
  } else {
    const bookedVisitQueryIn = { ...patientVisitQueryIn };
    if (isDefined(participantIds)) {
      bookedVisitQueryIn.criteria = { ...bookedVisitQueryIn.criteria, participantIds };
    }

    const bookedVisits: BookedVisitsPage = await VisitService.queryBookedVisits(
      bookedVisitQueryIn,
      allVisitsRequests,
      page,
      apiOptions,
    );

    // Only query for the patients if we don't already have it.
    const patients = patientVisitQueryIn.criteria?.patient
      ? [patientVisitQueryIn.criteria.patient]
      : await queryPatientsByBookedVisits(bookedVisits, study, studyConfig);

    const visitsContent = StudyContentService.getScheduleVisitsContent();
    const visitRequests = await queryVisitRequestsByPatients(patients, studyConfig);

    const bioSamplesForAllVisits: BioSample[] = await queryBioSamplesIfEnabled(
      patientVisitQueryIn,
      visitRequests,
      bookedVisits,
    );

    const kitTypesAndDescsForAllVisits = await queryKitDescByTypesIfEnabled(visitRequests, patientVisitQueryIn);

    const bookedPatientVisits = await Promise.all(
      bookedVisits.visits.map(async visit => {
        const patient = patients.find(patient => patient.userId === visit.participants[0]?.userId);

        if (patient) {
          const rolloutRequestResult = await VisitScheduleRolloutService.getRolloutRequestResult({
            visit,
            visitRequests,
            patient,
            study,
          });
          const bookedVisit = BookedPatientVisitMapper.from({
            visit,
            visitRequests,
            patient,
            studyVisitConfig: studyConfig.visitConfig,
            patientVisitTexts,
            unitTimeTexts,
            visitsContent,
            bioSamples: bioSamplesForAllVisits.filter(bioSample => bioSample.visitId === visit.visitId),
            allKitsWithDesc: kitTypesAndDescsForAllVisits,
            rolloutRequestResult,
          });
          return bookedVisit;
        } else {
          console.warn(`No patient found for visit with ID ${visit.visitId}`);
          return undefined;
        }
      }),
    );

    return {
      bookedVisits: bookedPatientVisits.filter(isDefined),
      total: bookedVisits.paging.count ?? 0,
      next: bookedVisits.paging.next,
      hasTooManyFreeTextResults: false,
      pageSize: page.limit,
    };
  }
};

const fetchByIds = async (
  patient: Patient,
  visitIds: string[],
  study: Study,
  studyConfig: StudyConfig,
  includeFields: BookedPatientVisitIncludeFields = {},
  apiOptions?: ApiOptions,
): Promise<BookedPatientVisit[]> => {
  const visitsQuery: BookedPatientVisitQueryIn = {
    criteria: {
      patient,
      visitIds,
    },
    includeFields,
  };

  const { bookedVisits } = await query(study, studyConfig, visitsQuery, largePage(), apiOptions);

  return bookedVisits;
};

const fetchAllByIds = async (
  visitIds: string[],
  study: Study,
  studyConfig: StudyConfig,
  includeFields: BookedPatientVisitIncludeFields = {},
  apiOptions?: ApiOptions,
): Promise<BookedPatientVisit[]> => {
  const visitsQuery: BookedPatientVisitQueryIn = {
    criteria: {
      visitIds,
    },
    includeFields,
  };

  const bookedVisits: BookedPatientVisit[] = [];
  let resultPage: Maybe<BookedPatientVisitItemPage>;
  do {
    resultPage = await BookedPatientVisitsQueryService.query(
      study,
      studyConfig,
      visitsQuery,
      {
        limit: Page.maxLimit,
        count: true,
        next: resultPage?.next,
      },
      apiOptions,
    );
    bookedVisits.push(...resultPage.bookedVisits);
  } while (bookedVisits.length < (resultPage?.total ?? 0));

  return bookedVisits;
};

const fetchById = async (
  patient: Patient,
  visitId: string,
  study: Study,
  studyConfig: StudyConfig,
  includeFields: BookedPatientVisitIncludeFields = {},
  apiOptions?: ApiOptions,
): Promise<BookedPatientVisit> => {
  const [bookedVisit] = await fetchByIds(patient, [visitId], study, studyConfig, includeFields, apiOptions);

  if (bookedVisit) {
    return bookedVisit;
  } else {
    throw CspError.notFound(`BookedPatientVisit with id ${visitId} not found`);
  }
};

export const BookedPatientVisitsQueryService = {
  query,
  fetchById,
  fetchByIds,
  fetchAllByIds,
};
